import { jsx } from '@emotion/react';
import { Heading, HeadingLevel, Icon, Link, Text } from '@zapier/design-system';
import { Fragment } from 'react';
import { ACCOUNT_USAGE_LINK } from './constants';
import { Divider } from '../Divider';
import InAppSidebarError from './InAppSidebarError';
import PlanUsage from './PlanUsage/PlanUsage';
import UniversalNavLink from '../../UniversalNavLink';
import { isLinkSelected } from './utils';
import { labelStyles } from '@zapier/style-encapsulation';
import { useQueryAccount, useQueryAccountUsage, useUniversalNavContext, } from '../../../hooks';
import { useQueryAccountPlan } from '../../../../universal-layout/hooks';
import { boxSizing, interactiveReset, } from '../../../../utils/style-mixins/reset';
const infinitize = (number) => {
    return number === -1 ? Infinity : number;
};
const Styles = labelStyles('InAppSidebarFooter', {
    footerWrapper: Object.assign(Object.assign({}, boxSizing), { overflow: 'hidden', padding: '6px var(--zds-space-8, 8px)' }),
    tOSWrapper: Object.assign(Object.assign({}, boxSizing), { whiteSpace: 'initial', background: 'var(--zds-gray-warm-2, #F5F3EB)', border: `1px solid var(--zds-gray-warm-3, #ECE9DF)`, borderRadius: 10, padding: 15 }),
    tOSHeader: Object.assign(Object.assign({}, boxSizing), { display: 'flex', justifyContent: 'space-between', marginBottom: 20 }),
    tOSHeading: Object.assign(Object.assign({}, boxSizing), { display: 'flex', alignItems: 'center' }),
    tOSDismissalIconButton: Object.assign(Object.assign({}, interactiveReset), { display: 'flex', alignItems: 'center', '&:focus': {
            outline: `1px solid var(--zds-ui-primary-strongest, #503EBD)`,
        } }),
    tOSDismissalButton: Object.assign(Object.assign({}, interactiveReset), { color: `var(--zds-ui-primary-strongest, #503EBD)`, textDecoration: 'underline', '&:focus': {
            outline: `1px solid var(--zds-ui-primary-strongest, #503EBD)`,
        } }),
});
const FooterWrapper = ({ children }) => (jsx("div", { css: Styles.footerWrapper, "data-zds": true }, children));
const MaybeTOS = () => {
    const LATEST_VERSION_OF_TERMS = 'design-system-tos-notification-2022-11-15';
    const hasUserDismissedLatestToS = localStorage.getItem('versionOfTermsDismissed') ===
        LATEST_VERSION_OF_TERMS || false;
    const handleDismiss = () => {
        try {
            localStorage.setItem('versionOfTermsDismissed', LATEST_VERSION_OF_TERMS);
        }
        catch (_a) {
            console.log('Failed to save versionOfTermsDismissed to localStorage');
        }
    };
    const { getFullyQualifiedURL, shouldRenderTOS } = useUniversalNavContext();
    return shouldRenderTOS && hasUserDismissedLatestToS === false ? (jsx(Fragment, null,
        jsx(Divider, null),
        jsx("div", { css: Styles.tOSWrapper },
            jsx("div", { css: Styles.tOSHeader },
                jsx("div", { css: Styles.tOSHeading },
                    jsx(Icon, { "aria-hidden": true, color: "UiPrimaryStrongest", name: "alertInfoFill", size: 18 }),
                    jsx(HeadingLevel, null,
                        jsx(Text, { margin: "0 0 0 5px", tag: Heading, type: "SmallPrint3" }, "Updated Terms"))),
                jsx("button", { "aria-label": "Dismiss notification", css: Styles.tOSDismissalIconButton, onClick: handleDismiss, type: "button", "data-zds": true },
                    jsx(Icon, { "aria-hidden": true, color: "GrayWarm10", name: "formX", size: 24 }))),
            jsx(Text, { tag: "p", type: "SmallPrint4" },
                "We updated the",
                ' ',
                jsx(Link, { href: getFullyQualifiedURL('/legal/terms-of-service') }, "Zapier Terms of Service"),
                ' ',
                "on November 15, 2022. For existing users, these updated terms will be effective beginning on December 15, 2022. By continuing to use Zapier, you agree to these updated terms.",
                ' ',
                jsx("button", { css: Styles.tOSDismissalButton, type: "button", onClick: handleDismiss, "data-zds": true }, "Dismiss"))))) : null;
};
const Footer = ({ currentAccountId, selectedHref }) => {
    var _a;
    const { data: accountData, isError: isAccountError } = useQueryAccount({
        currentAccountId,
    });
    const { data: accountUsageData, isError: isAccountUsageError } = useQueryAccountUsage(currentAccountId);
    const { data: accountPlanData, isError: isAccountPlanError } = useQueryAccountPlan(currentAccountId, (_a = accountData === null || accountData === void 0 ? void 0 : accountData.plan) === null || _a === void 0 ? void 0 : _a.id);
    const renderedUsageLink = (jsx(UniversalNavLink, { href: ACCOUNT_USAGE_LINK.href, icon: typeof ACCOUNT_USAGE_LINK.icon === 'object'
            ? ACCOUNT_USAGE_LINK.icon.selected
            : ACCOUNT_USAGE_LINK.icon, isSelected: isLinkSelected(ACCOUNT_USAGE_LINK.href || '', selectedHref), isSidebar: true, text: ACCOUNT_USAGE_LINK.text }));
    if (!accountData)
        return null;
    if (isAccountError || isAccountPlanError || isAccountUsageError) {
        return (jsx(Fragment, null,
            renderedUsageLink,
            jsx(FooterWrapper, null,
                jsx(InAppSidebarError, null, "An error has occurred"),
                jsx(MaybeTOS, null))));
    }
    if (!accountUsageData || !accountPlanData) {
        return null;
    }
    const { plan, isTrialing, trialEndDate, isTrialingTeams, teamTrialEndDate, isTrialingCompanies, companiesTrialEndDate, isTrialingEnterprise, enterpriseTrialEndDate, isWorkspace, } = accountData;
    const { meteredPricingTiers: tiers } = accountPlanData;
    const planUsageTitle = isWorkspace ? accountData.name : `${plan.name} Plan`;
    const freeTier = tiers && tiers.find((tier) => tier.pricePerTask === 0);
    const numFreeTasksMax = freeTier ? freeTier.numTasks : 0;
    const normalizedUsage = {
        isPlanMetered: plan.hasMeteredBilling,
        numTasks: accountUsageData.tasks.total,
        numFreeTasksMax,
        numTasksMax: infinitize(accountUsageData.tasks.limit),
        numZaps: accountUsageData.zaps.on,
        numZapsMax: infinitize(accountUsageData.zaps.limit),
    };
    const trialPlanName = isTrialingTeams
        ? 'Team'
        : isTrialingCompanies
            ? 'Company'
            : isTrialingEnterprise
                ? 'Enterprise'
                : isTrialing
                    ? 'Professional'
                    : undefined;
    const trialPlanEndDate = isTrialingTeams
        ? teamTrialEndDate
        : isTrialingCompanies
            ? companiesTrialEndDate
            : isTrialingEnterprise
                ? enterpriseTrialEndDate
                : trialEndDate;
    return (jsx(FooterWrapper, null,
        jsx(PlanUsage, Object.assign({}, normalizedUsage, { resetDate: accountUsageData.periodEnd, title: planUsageTitle, trialPlanName: trialPlanName, trialPlanEndDate: trialPlanEndDate !== null && trialPlanEndDate !== void 0 ? trialPlanEndDate : undefined, isWorkspace: isWorkspace }),
            jsx(MaybeTOS, null))));
};
export default Footer;
//# sourceMappingURL=Footer.js.map
// @zapier/universal-layout@11.2.3 (b1a6e8603)
